import { BOSS_IDS } from '@bioforge/protocol';
import { Sprite, Texture } from 'pixi.js';
import {
  LOCKED_BOSS_SVG,
  LOGO_SVG,
  MICROBE_SVG,
  armorSvg,
  bossSvg,
  hatSvg,
} from '../art/characters.js';
import { ICONS } from './icons.js';

/**
 * Персонажи и значки — SVG, которые браузер растрирует в текстуры при запуске.
 * Так рисунок совпадает с макетом один в один, а Pixi только размещает и анимирует его.
 */

interface Art {
  readonly markup: string;
  /** Размер растра, px: с запасом, чтобы крупный показ оставался чётким. */
  readonly size: number;
  /** Высота растра для неквадратных рисунков (кадры сцен); по умолчанию — size. */
  readonly height?: number;
}

const ART = new Map<string, Art>();
const TEXTURES = new Map<string, Texture>();

function register(key: string, markup: string, size: number): void {
  ART.set(key, { markup, size });
}

MICROBE_SVG.forEach((markup, color) => register(`microbe-${color}`, markup, 192));
register('armor', armorSvg(), 192);
// Скины микробов: колпак общий, очки — под глаза каждого вида.
register('hat-santa', hatSvg('santa', 0), 256);
MICROBE_SVG.forEach((_, color) => register(`hat-shades-${color}`, hatSvg('shades', color), 256));
register('armor-gold', armorSvg(true), 192);
for (const id of BOSS_IDS) {
  register(`boss-${id}`, bossSvg(id), 384);
  register(`boss-${id}-scream`, bossSvg(id, 'scream'), 384);
}
register('boss-locked', LOCKED_BOSS_SVG, 192);
register('logo', LOGO_SVG, 256);
for (const [key, markup] of Object.entries(ICONS)) register(`icon-${key}`, markup, 96);

export type ArtKey = string;

async function rasterize(art: Art): Promise<Texture> {
  const height = art.height ?? art.size;
  const sized = art.markup.replace('<svg ', `<svg width="${art.size}" height="${height}" `);
  const image = new Image(art.size, height);
  image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(sized)}`;
  await image.decode();
  return Texture.from(image);
}

/** Растрирует все рисунки; вызывается один раз до первого экрана. */
export async function loadArt(): Promise<void> {
  await Promise.all(
    [...ART].map(async ([key, art]) => {
      if (!TEXTURES.has(key)) TEXTURES.set(key, await rasterize(art));
    }),
  );
}

/**
 * Растрирует рисунок, известный только после загрузки контента (карточки каталога, кадры сцен).
 * Повторный вызов с тем же ключом ничего не делает.
 */
export async function ensureArt(
  key: ArtKey,
  markup: string,
  size: number,
  height?: number,
): Promise<void> {
  if (TEXTURES.has(key)) return;
  const art: Art = { markup, size, ...(height ? { height } : {}) };
  ART.set(key, art);
  TEXTURES.set(key, await rasterize(art));
}

export function artTexture(key: ArtKey): Texture {
  return TEXTURES.get(key) ?? Texture.EMPTY;
}

/** Спрайт рисунка размером `size` × `size` с центром в (0, 0). */
export function artSprite(key: ArtKey, size: number): Sprite {
  const sprite = new Sprite(artTexture(key));
  sprite.anchor.set(0.5);
  sprite.width = size;
  sprite.height = size;
  return sprite;
}

/** Ключи рисунков (проверяются тестом: каждый SVG корректен). */
export function artMarkup(): ReadonlyMap<string, string> {
  return new Map([...ART].map(([key, art]) => [key, art.markup]));
}
